import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';

export type TestPlane = 'sagittal' | 'frontal' | 'transverse' | 'sagittal/transverse' | 'frontal/transverse';

export type GoalType = 'number' | 'range' | 'boolean';

export interface NumericGoal {
  type: 'number';
  value: number;
}

export interface RangeGoal {
  type: 'range';
  min: number;
  max: number;
}

export interface BooleanGoal {
  type: 'boolean';
  passLabel: string;
}

export type AssessmentGoal = NumericGoal | RangeGoal | BooleanGoal;

export interface SopAssessmentDefinition {
  key: string;
  label: string;
  plane: TestPlane;
  goal: AssessmentGoal;
}

// Stability guardrail: ROM above 120% of the target is treated as laxity risk.
export const HYPERMOBILITY_THRESHOLD = 1.2;

export const SLR_APT_HIGH_THRESHOLD = 70;
export const SLR_SB_LOW_THRESHOLD = 45;
export const HIP_ABDUCTION_APT_LOW_THRESHOLD = 30;
export const SHOULDER_ABDUCTION_SB_LOW_THRESHOLD = 30;
export const SHOULDER_IR_APT_LOW_THRESHOLD = 70;

export const SOP_ASSESSMENTS: ReadonlyArray<SopAssessmentDefinition> = [
  {
    key: 'CervicalAxialRotation',
    label: 'Cervical Axial Rotation',
    plane: 'transverse',
    goal: { type: 'number', value: 30 },
  },
  {
    key: 'CervicalLateralFlexion',
    label: 'Cervical Lateral Flexion',
    plane: 'frontal',
    goal: { type: 'number', value: 30 },
  },
  {
    key: 'ShoulderIR',
    label: 'Shoulder Internal Rotation',
    plane: 'transverse',
    goal: { type: 'number', value: 70 },
  },
  {
    key: 'ShoulderER',
    label: 'Shoulder External Rotation',
    plane: 'transverse',
    goal: { type: 'range', min: 60, max: 90 },
  },
  {
    key: 'ShoulderFlexion',
    label: 'Shoulder Flexion',
    plane: 'sagittal',
    goal: { type: 'number', value: 120 },
  },
  {
    key: 'ShoulderAbduction',
    label: 'Shoulder Abduction',
    plane: 'frontal',
    goal: { type: 'range', min: 45, max: 60 },
  },
  {
    key: 'StraightLegRaise',
    label: 'Straight Leg Raise (SLR)',
    plane: 'sagittal/transverse',
    goal: { type: 'number', value: 90 },
  },
  {
    key: 'InfrasternalAngle',
    label: 'Infrasternal Angle',
    plane: 'frontal/transverse',
    goal: { type: 'number', value: 90 },
  },
  {
    key: 'HipExtension',
    label: 'Hip Extension',
    plane: 'sagittal',
    goal: { type: 'number', value: 0 },
  },
  {
    key: 'PelvicRotation',
    label: 'Pelvis Rotation',
    plane: 'transverse',
    goal: { type: 'number', value: 70 },
  },
  {
    key: 'FemoralIR',
    label: 'Femoral Internal Rotation',
    plane: 'transverse',
    goal: { type: 'range', min: 30, max: 45 },
  },
  {
    key: 'FemoralER',
    label: 'Femoral External Rotation',
    plane: 'transverse',
    goal: { type: 'range', min: 45, max: 60 },
  },
  {
    key: 'HipAbduction',
    label: 'Hip Abduction',
    plane: 'frontal',
    goal: { type: 'number', value: 45 },
  },
];

export const SOP_ASSESSMENTS_LEGACY: ReadonlyArray<SopAssessmentDefinition> = Object.freeze(
  SOP_ASSESSMENTS.map((item) =>
    item.key === 'HipExtension'
      ? {
          key: 'HipAdductionObers',
          label: "Hip Adduction (Ober's)",
          plane: 'sagittal' as const,
          goal: { type: 'boolean', passLabel: 'Knee to Table' } as const,
        }
      : item
  )
);

export const SOP_ASSESSMENT_BY_KEY: Readonly<Record<string, SopAssessmentDefinition>> = Object.freeze(
  SOP_ASSESSMENTS.reduce<Record<string, SopAssessmentDefinition>>((acc, item) => {
    acc[item.key] = item;
    return acc;
  }, {})
);

export const SOP_ASSESSMENT_BY_KEY_LEGACY: Readonly<Record<string, SopAssessmentDefinition>> = Object.freeze(
  SOP_ASSESSMENTS_LEGACY.reduce<Record<string, SopAssessmentDefinition>>((acc, item) => {
    acc[item.key] = item;
    return acc;
  }, {})
);

export const getSopAssessments = (version: RuleDocVersion): ReadonlyArray<SopAssessmentDefinition> =>
  version === 'legacy' ? SOP_ASSESSMENTS_LEGACY : SOP_ASSESSMENTS;

export const getSopAssessmentByKey = (version: RuleDocVersion): Readonly<Record<string, SopAssessmentDefinition>> =>
  version === 'legacy' ? SOP_ASSESSMENT_BY_KEY_LEGACY : SOP_ASSESSMENT_BY_KEY;

